// frontend/src/audio/useDenoiser.ts
// React hook for the Moment DeepFilterNet3 denoiser.
//
// API :
//   - Modes : 'off' / 'on' (DeepFilterNet3 has no internal VAD)
//   - localStorage key : 'moment-denoise-mode'
//   - Node creation is delegated to `denoiseEngine.createNode(ctx)`
//   - Mode sync goes through `denoiseEngine.setMode()` (which forwards to the
//     current worklet node port).
//
// Usage :
//   const denoiser = useDenoiser()
//   // ...inside a WebAudio pipeline setup :
//   const node = await denoiser.createNode(ctx)
//   if (node) source.connect(node).connect(dest)
//   // ...and from a toggle UI :
//   <DenoiseToggle denoiser={denoiser} />
//
// The hook does NOT eagerly call `denoiseEngine.init()` — models are only
// loaded the first time a component actually needs a node (via createNode).

import { useCallback, useEffect, useRef, useState } from 'react'
import { denoiseEngine } from './DenoiseEngine'
import { denoiseLog } from './denoiseLog'

export type DenoiseMode = 'off' | 'on'

const LS_KEY = 'moment-denoise-mode'

export interface UseDenoiserReturn {
  /** ONNX models are loaded and the Worker is ready. */
  isReady: boolean
  /** Engine is currently loading the Worker and/or the models. */
  isLoading: boolean
  /** `true` if the user has enabled denoising (mode === 'on'). */
  isActive: boolean
  /** Last engine error (worker crash, model load failure…). */
  error: string | null
  /** Persisted user preference for this device. */
  mode: DenoiseMode
  /** Set the mode and persist it to localStorage. */
  setMode: (m: DenoiseMode) => void
  /** Flip between 'on' and 'off'. */
  toggle: () => void
  /** Create an AudioWorkletNode connected to the shared Worker. */
  createNode: (ctx: AudioContext) => Promise<AudioWorkletNode | null>
  /** Disconnect and drop the internal node reference. */
  destroyNode: () => void
  /** Stable ref to the active node, if any. */
  nodeRef: React.RefObject<AudioWorkletNode | null>
}

function readInitialMode(): DenoiseMode {
  try {
    const saved = localStorage.getItem(LS_KEY)
    if (saved === 'off') return 'off'
  } catch { /* localStorage unavailable */ }
  return 'on'
}

export function useDenoiser(context = 'visio'): UseDenoiserReturn {
  const [mode, setModeState] = useState<DenoiseMode>(readInitialMode)
  const [isReady, setIsReady] = useState<boolean>(denoiseEngine.isReady)
  const [isLoading, setIsLoading] = useState<boolean>(denoiseEngine.isLoading)
  const [error, setError] = useState<string | null>(denoiseEngine.error)

  const nodeRef = useRef<AudioWorkletNode | null>(null)
  const modeRef = useRef<DenoiseMode>(mode)

  // Keep modeRef in sync so async callbacks always see the latest value
  useEffect(() => { modeRef.current = mode }, [mode])

  // Pull the current Engine state into React state.
  const refreshEngineState = useCallback(() => {
    setIsReady(denoiseEngine.isReady)
    setIsLoading(denoiseEngine.isLoading)
    setError(denoiseEngine.error)
  }, [])

  const setMode = useCallback((m: DenoiseMode) => {
    setModeState(m)
    modeRef.current = m
    try { localStorage.setItem(LS_KEY, m) } catch { /* ignore */ }
    denoiseEngine.setMode(m)
    denoiseLog({ step: 'setMode', context, mode: m })
  }, [context])

  const toggle = useCallback(() => {
    setMode(modeRef.current === 'off' ? 'on' : 'off')
  }, [setMode])

  const createNode = useCallback(
    async (ctx: AudioContext): Promise<AudioWorkletNode | null> => {
      // Tear down any previous node held by this hook instance
      if (nodeRef.current) {
        try { nodeRef.current.disconnect() } catch { /* ignore */ }
        nodeRef.current = null
      }
      // Reflect the "loading" transition immediately so UI can spin
      setIsLoading(true)
      denoiseLog({ step: 'createNode_start', context })
      try {
        const node = await denoiseEngine.createNode(ctx)
        // Re-apply the persisted mode to the new node
        denoiseEngine.setMode(modeRef.current)
        nodeRef.current = node
        refreshEngineState()
        denoiseLog({ step: 'createNode_ok', context })
        return node
      } catch (err) {
        const message = (err as Error).message || 'denoise createNode failed'
        setError(message)
        setIsLoading(false)
        setIsReady(false)
        console.warn('[moment-denoise] createNode failed:', err)
        denoiseLog({ step: 'createNode_error', context, detail: message })
        return null
      }
    },
    [refreshEngineState],
  )

  const destroyNode = useCallback(() => {
    if (nodeRef.current) {
      // Send RTF telemetry before cleanup (fire-and-forget)
      denoiseEngine.getMetrics().then((m) => {
        if (m.batches === 0) return
        try {
          navigator.sendBeacon('/api/denoise/telemetry', JSON.stringify({
            ...m,
            context,
            user_agent: navigator.userAgent.replace(/^.*?(Chrome\/\d+|Firefox\/\d+|Safari\/\d+).*$/, '$1') || navigator.userAgent.slice(0, 60),
          }))
        } catch { /* sendBeacon may fail during unload */ }
      }).catch(() => {})
      denoiseLog({ step: 'destroyNode', context })
      try { nodeRef.current.disconnect() } catch { /* ignore */ }
      nodeRef.current = null
    }
  }, [context])

  // Sync mode → Engine whenever `mode` changes (covers external setMode calls
  // that bypass the local callback, e.g. via a parent replacing the state).
  useEffect(() => {
    denoiseEngine.setMode(mode)
  }, [mode])

  // On mount : read the Engine state once. If the Engine is loading because
  // another component already kicked off init(), poll briefly until the
  // transition completes so the UI stops spinning.
  useEffect(() => {
    refreshEngineState()
    if (denoiseEngine.isLoading && !denoiseEngine.isReady) {
      const id = window.setInterval(() => {
        refreshEngineState()
        if (!denoiseEngine.isLoading) {
          window.clearInterval(id)
        }
      }, 100)
      return () => { window.clearInterval(id) }
    }
    return undefined
  }, [refreshEngineState])

  // Cleanup on unmount : disconnect the node held by this hook. The Engine
  // itself (Worker, models) is NOT torn down — other components may still be
  // using the shared singleton.
  useEffect(() => {
    return () => { destroyNode() }
  }, [destroyNode])

  return {
    isReady,
    isLoading,
    isActive: mode !== 'off',
    error,
    mode,
    setMode,
    toggle,
    createNode,
    destroyNode,
    nodeRef,
  }
}
